'use client'

import * as React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft,
  BookmarkPlus,
  Calendar,
  Clock,
  Coins,
  ExternalLink,
  FileText,
  Film,
  ImageIcon,
  Layers,
  Loader2,
  MonitorPlay,
  RefreshCw,
  Star,
  Timer,
  Trash2,
  Video,
  XCircle,
} from 'lucide-react'

import { StatusBadge } from '@/components/jobs/StatusBadge'
import { PromptBlock } from '@/components/jobs/PromptBlock'
import { DownloadButton } from '@/components/gallery/DownloadButton'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useToast } from '@/components/ui/use-toast'
import { useFolders } from '@/hooks/useAssets'
import { apiFetch, apiJson } from '@/lib/api'
import { formatDate } from '@/lib/utils'
import type {
  AspectRatio,
  GenerateRequest,
  GenerationJob,
  JobDetailResponse,
  SceneAssembly,
  VideoModel,
} from '@/types'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDuration(s: number | null | undefined): string {
  if (s == null) return '-'
  if (s < 60) return `${s.toFixed(1)}s`
  const m = Math.floor(s / 60)
  return `${m}m ${(s % 60).toFixed(0)}s`
}

function formatCost(cents: number | null | undefined): string {
  if (cents == null) return '-'
  if (cents < 100) return `${cents.toFixed(1)} ¢`
  return `$${(cents / 100).toFixed(2)}`
}

function formatFullDate(value: string | null | undefined): string {
  if (!value) return '-'
  return new Date(value).toLocaleString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function MetaRow({
  icon: Icon,
  label,
  children,
}: {
  icon: React.ElementType
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-start gap-3 py-2">
      <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
        <div className="mt-0.5 text-sm">{children}</div>
      </div>
    </div>
  )
}

function EmptySection({ message }: { message: string }) {
  return (
    <div className="flex flex-col items-center gap-2 py-12 text-center">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Save to Library widget (reusable across tabs)
// ---------------------------------------------------------------------------

function SaveToLibrary({ jobId, onSaved }: { jobId: number; onSaved?: () => void }) {
  const { toast } = useToast()
  const { folders } = useFolders()
  const [open, setOpen] = React.useState(false)
  const [folderId, setFolderId] = React.useState<string>('')
  const [saving, setSaving] = React.useState(false)

  const handleSave = async () => {
    if (!folderId) return
    setSaving(true)
    try {
      await apiJson(`/api/v1/jobs/${jobId}/save-to-library`, {
        method: 'POST',
        body: JSON.stringify({ folder_id: Number(folderId) }),
      })
      toast({ title: 'Saved to library', description: 'Output added to the selected folder.' })
      setOpen(false)
      setFolderId('')
      onSaved?.()
    } catch (err) {
      toast({ title: 'Save failed', description: (err as Error).message, variant: 'destructive' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-2">
      <Button variant="outline" size="sm" onClick={() => setOpen((v) => !v)}>
        <BookmarkPlus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
        Save to Library
      </Button>
      {open && (
        <div className="rounded-md border border-border bg-muted/30 p-3 space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Select folder</Label>
            <Select value={folderId} onValueChange={setFolderId} disabled={saving}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a folder..." />
              </SelectTrigger>
              <SelectContent>
                {folders.map((f) => (
                  <SelectItem key={f.id} value={String(f.id)}>
                    {f.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={!folderId || saving}>
              {saving ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Saving...</> : 'Save'}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setOpen(false)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Overview
// ---------------------------------------------------------------------------

function OverviewTab({
  job,
  starred,
  onToggleStar,
  onCancel,
  onDelete,
  onRegenerate,
  regenerating,
}: {
  job: JobDetailResponse
  starred: boolean
  onToggleStar: () => void
  onCancel: () => void
  onDelete: () => void
  onRegenerate: () => void
  regenerating: boolean
}) {
  const active = job.status === 'queued' || job.status === 'processing'

  // Strip localhost prefixes so videos that were stored with a hard-coded
  // BASE_URL still play on the prod host (private-address-space CORS block).
  const videoUrl = job.output_type === 'video' ? videoSrc(job.output_url) || null : null
  const imageUrl = job.output_type === 'image' ? videoSrc(job.output_url) || null : null

  // Pull the parent assembly (when this job is part of one) so we can
  // surface per-scene + stitching errors in the Issues banner below.
  // Without this the user only sees the assembly's rolled-up message
  // ("3 scene(s) failed: #3, #5, #6") with no per-scene detail.
  const [assemblyForErrors, setAssemblyForErrors] = React.useState<SceneAssembly | null>(null)
  React.useEffect(() => {
    if (!job.assembly_id) return
    let cancelled = false
    apiJson<SceneAssembly>(`/api/v1/assembly/${job.assembly_id}`)
      .then((data) => {
        if (!cancelled) setAssemblyForErrors(data)
      })
      .catch(() => {
        /* silent — the banner just won't show extra detail */
      })
    return () => {
      cancelled = true
    }
  }, [job.assembly_id])

  // Map raw model_id slug to the catalog's friendly name (which now carries
  // the provider suffix in brackets after Phase 5 — e.g. "Nano Banana Pro
  // (Google)"). Fetch is cached for the page lifetime.
  const [modelCatalog, setModelCatalog] = React.useState<VideoModel[] | null>(null)
  React.useEffect(() => {
    apiJson<{ items: VideoModel[] }>('/api/v1/models')
      .then(({ items }) => setModelCatalog(items))
      .catch(() => setModelCatalog([]))
  }, [])
  const modelEntry = modelCatalog?.find((m) => m.id === job.model_id) ?? null
  const modelDisplay = modelEntry?.name ?? job.model_id

  // Collect every error worth surfacing on the Overview tab — the job's
  // own error_message, each failed scene's error_message, and any
  // stitching error from the assembly itself. The Outputs tab already
  // shows per-scene errors next to each scene chip; this banner exists
  // so the user doesn't have to click into Outputs to see what broke.
  const overviewIssues: { label: string; message: string }[] = []
  if (job.error_message && job.status === 'failed') {
    overviewIssues.push({ label: 'Job', message: job.error_message })
  }
  if (assemblyForErrors) {
    for (const scene of assemblyForErrors.scenes ?? []) {
      if (scene.status === 'failed' && scene.error_message) {
        overviewIssues.push({
          label: `Scene ${scene.scene_number}`,
          message: scene.error_message,
        })
      }
    }
    // The assembly-level error_message contains stitching failures when
    // assembly.status is 'failed' but no scene-level errors explain it —
    // only show it if it isn't already implied by the per-scene errors.
    if (
      assemblyForErrors.status === 'failed' &&
      assemblyForErrors.error_message &&
      !overviewIssues.some((i) => i.message === assemblyForErrors.error_message)
    ) {
      overviewIssues.push({
        label: 'Assembly',
        message: assemblyForErrors.error_message,
      })
    }
  }

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <StatusBadge status={job.status} />
          <Badge variant="outline">{job.aspect_ratio}</Badge>
          <Badge variant="outline" className="uppercase text-[10px]">{job.output_type}</Badge>
          <Badge variant="outline">{job.duration_seconds}s</Badge>
          {job.cost_cents != null && (
            <span className="text-xs text-muted-foreground">{formatCost(job.cost_cents)}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            aria-label={starred ? 'Unstar job' : 'Star job'}
            onClick={onToggleStar}
          >
            <Star
              className={`h-4 w-4 ${starred ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
              aria-hidden="true"
            />
          </Button>
          {active && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={onCancel}
            >
              <XCircle className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              Cancel
            </Button>
          )}
          {!active && (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:bg-destructive hover:text-destructive-foreground"
              onClick={onDelete}
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Issues banner — surfaces every failed scene + stitching error in
          one place so the user doesn't have to dig through the Outputs tab
          to learn which beats broke. Only renders when there's something
          to show; otherwise this slot stays clean. */}
      {overviewIssues.length > 0 && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive space-y-1.5"
        >
          <p className="font-semibold">
            {overviewIssues.length === 1
              ? 'Issue'
              : `${overviewIssues.length} issues`}
          </p>
          <ul className="space-y-1">
            {overviewIssues.map((issue, i) => (
              <li key={i} className="text-xs text-destructive/90">
                <span className="font-medium">{issue.label}:</span>{' '}
                <span className="whitespace-pre-wrap break-words">{issue.message}</span>
              </li>
            ))}
          </ul>
          {job.assembly_id != null && (
            <p className="text-xs text-destructive/80">
              See the <span className="font-medium text-destructive">Outputs</span> tab to inspect
              per-scene state and retry options.
            </p>
          )}
        </div>
      )}

      {/* Prompts — when the job came from a script, show BOTH the user's
          original brief AND the engineered Seedance prompt that was actually
          sent to the model. When there's no script, the job's prompt_text IS
          what the user typed, so show it once under "Prompt". For stitched
          assembly jobs the prompt_text is a "[Stitched: ...]" placeholder,
          not a real Seedance prompt — point the user at the Outputs tab
          where the per-scene engineered prompts live. */}
      {job.output_kind === 'stitched' && job.script?.source_prompt ? (
        <>
          <PromptBlock
            label="User brief"
            text={job.script.source_prompt}
          />
          <div className="rounded-md border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
            This is the stitched assembly job. The actual Seedance prompts
            were sent per-scene — see the <span className="font-medium text-foreground">Outputs</span> tab
            to browse each scene's engineered prompt + attachments.
          </div>
        </>
      ) : job.script?.source_prompt ? (
        <>
          <PromptBlock
            label="User brief"
            text={job.script.source_prompt}
          />
          <PromptBlock
            label="Engineered prompt (sent to Seedance)"
            text={job.prompt_text}
            variant="muted"
          />
          {job.built_prompt && job.built_prompt !== job.prompt_text && (
            <PromptBlock
              label="Final prompt (style + context injected)"
              text={job.built_prompt}
              variant="muted"
            />
          )}
        </>
      ) : (
        <PromptBlock
          label="Prompt"
          text={job.prompt_text}
        />
      )}

      {/* Active spinner */}
      {active && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
          <span className="capitalize">{job.status}…</span>
        </div>
      )}

      {/* Error + Regenerate */}
      {job.status === 'failed' && (
        <div className="space-y-3">
          {job.error_message && (
            <div
              className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
              role="alert"
            >
              {job.error_message}
            </div>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={onRegenerate}
            disabled={regenerating}
          >
            {regenerating ? (
              <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />Submitting...</>
            ) : (
              <><RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Regenerate</>
            )}
          </Button>
        </div>
      )}

      {/* Video output */}
      {videoUrl && (
        <div className="space-y-2">
          <video
            src={videoUrl}
            className="w-full rounded-lg border border-border bg-black"
            controls
            preload="metadata"
            aria-label="Generated video output"
          />
          <div className="flex flex-wrap gap-2">
            <DownloadButton url={videoUrl} filename={`creativeos-job-${job.id}.mp4`} />
            <SaveToLibrary jobId={job.id} />
          </div>
        </div>
      )}

      {/* Image output */}
      {imageUrl && (
        <div className="space-y-2">
          <img
            src={imageUrl}
            alt="Generated output"
            className="w-full rounded-lg border border-border"
            loading="lazy"
          />
          <div className="flex flex-wrap gap-2">
            <DownloadButton url={imageUrl} filename={`creativeos-job-${job.id}.jpg`} />
            <SaveToLibrary jobId={job.id} />
          </div>
        </div>
      )}

      <Separator />

      {/* Meta grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <MetaRow icon={MonitorPlay} label="Model">
          <span className="text-sm" title={job.model_id}>{modelDisplay}</span>
        </MetaRow>
        <MetaRow icon={Layers} label="Style Profile">
          {job.style_profile.replace(/_/g, ' ')}
        </MetaRow>
        <MetaRow icon={Film} label="Aspect Ratio">
          <Badge variant="outline">{job.aspect_ratio}</Badge>
        </MetaRow>
        <MetaRow icon={Timer} label="Duration">
          {job.duration_seconds}s
        </MetaRow>
        <MetaRow icon={Coins} label="Cost">
          {formatCost(job.cost_cents)}
        </MetaRow>
        <MetaRow icon={Clock} label="Gen Time">
          {formatDuration(job.generation_time_seconds)}
        </MetaRow>
        <MetaRow icon={Calendar} label="Created">
          {formatFullDate(job.created_at)}
        </MetaRow>
        {job.completed_at && (
          <MetaRow icon={Calendar} label="Completed">
            {formatFullDate(job.completed_at)}
          </MetaRow>
        )}
      </div>

      {/* Settings used (assembly jobs only) — surfaces the exact wizard
          choices so users can audit "what did I select that produced this
          output?" without going back to Create Ad. */}
      {assemblyForErrors && <AssemblySettingsCard assembly={assemblyForErrors} />}
    </div>
  )
}

// ---------------------------------------------------------------------------
// AssemblySettingsCard — wizard choices walkthrough on JobDetail
// ---------------------------------------------------------------------------

function AssemblySettingsCard({ assembly }: { assembly: SceneAssembly }) {
  const params = (assembly.generation_params ?? {}) as Record<string, unknown>
  const bridgeEnabled = params.bridge_enabled === true
  const fastPreview = params.fast_preview === true
  const sceneVideoModes = Array.isArray(params.scene_video_modes)
    ? (params.scene_video_modes as unknown[]).filter(
        (m): m is string => typeof m === 'string',
      )
    : []

  const sceneStyles = assembly.scenes.map((s) => s.style_profile ?? '—')
  const hasMixedScenes =
    sceneStyles.length > 1 && sceneStyles.some((m) => m !== sceneStyles[0])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Settings used</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <SettingRow
          label="Same person across scenes (bridge)"
          hint="When ON, every scene starts from the previous scene's last frame so the same person, outfit and setting carry across cuts."
          value={
            <Badge variant={bridgeEnabled ? 'default' : 'outline'}>
              {bridgeEnabled ? 'On' : 'Off'}
            </Badge>
          }
        />
        <Separator />
        <SettingRow
          label="Fast preview"
          hint="Swaps to the fast model variant, caps resolution to 480p, caps each scene to 5s, disables auto-stitch."
          value={
            <Badge variant={fastPreview ? 'default' : 'outline'}>
              {fastPreview ? 'On' : 'Off'}
            </Badge>
          }
        />
        <Separator />
        <SettingRow
          label="Auto-stitch final cut"
          value={
            <Badge variant={assembly.auto_stitch ? 'default' : 'outline'}>
              {assembly.auto_stitch ? 'On' : 'Off'}
            </Badge>
          }
        />
        {assembly.auto_stitch && assembly.transition_seconds != null && (
          <>
            <Separator />
            <SettingRow
              label="Scene transition"
              hint="Crossfade duration between adjacent scenes at stitch time. 0 = hard cuts."
              value={
                <span className="font-medium">
                  {assembly.transition_seconds === 0
                    ? 'Hard cut'
                    : `${assembly.transition_seconds}s crossfade`}
                </span>
              }
            />
          </>
        )}
        {assembly.filmography_profile && (
          <>
            <Separator />
            <SettingRow
              label="Filmography profile"
              value={
                <span className="font-mono text-xs">
                  {assembly.filmography_profile.replace(/_/g, ' ')}
                </span>
              }
            />
          </>
        )}
        {(hasMixedScenes || sceneVideoModes.length > 0) && (
          <>
            <Separator />
            <div className="flex items-start justify-between gap-3">
              <span className="text-muted-foreground shrink-0">Per-scene styles</span>
              <div className="flex gap-1 flex-wrap justify-end max-w-[60%]">
                {(sceneVideoModes.length > 0 ? sceneVideoModes : sceneStyles).map(
                  (mode, i) => (
                    <Badge
                      key={`scene-style-${i}`}
                      variant="outline"
                      className="text-[10px] font-mono"
                      title={`Scene ${i + 1}: ${mode}`}
                    >
                      S{i + 1}: {String(mode).replace(/_/g, ' ')}
                    </Badge>
                  ),
                )}
              </div>
            </div>
          </>
        )}
        <Separator />
        <SettingRow
          label="Scene count"
          value={<span className="font-medium">{assembly.scenes.length}</span>}
        />
      </CardContent>
    </Card>
  )
}

function SettingRow({
  label,
  hint,
  value,
}: {
  label: string
  hint?: string
  value: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-muted-foreground" title={hint}>
        {label}
      </span>
      {value}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Script
// ---------------------------------------------------------------------------

function ScriptTab({ job }: { job: JobDetailResponse }) {
  const script = job.script

  if (!script) {
    return <EmptySection message="No script attached to this job." />
  }

  const statusColor =
    script.status === 'approved'
      ? 'bg-green-500/15 text-green-600 border-green-500/30'
      : 'bg-secondary text-secondary-foreground border-border'

  return (
    <div className="space-y-6">
      {/* Script header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h2 className="text-lg font-semibold">{script.title}</h2>
          {script.active_version_number != null && (
            <p className="text-xs text-muted-foreground">Version {script.active_version_number}</p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium capitalize ${statusColor}`}
            aria-label={`Script status: ${script.status}`}
          >
            {script.status}
          </span>
          <Button asChild variant="outline" size="sm">
            <Link to={`/create?script_id=${script.id}&step=2`}>
              <ExternalLink className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
              Open in editor
            </Link>
          </Button>
        </div>
      </div>

      <Separator />

      {/* Scenes */}
      {script.active_version_scenes.length === 0 ? (
        <EmptySection message="No scenes in the active version." />
      ) : (
        <div className="space-y-3">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Scenes ({script.active_version_scenes.length})
          </p>
          {script.active_version_scenes.map((scene) => (
            <Card key={scene.id} className="border-border">
              <CardHeader className="pb-1 pt-4 px-4">
                <div className="flex items-center justify-between gap-2">
                  <CardTitle className="text-sm">Scene {scene.scene_number}</CardTitle>
                  <Badge variant="outline">{scene.duration_seconds}s</Badge>
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-2">
                {scene.description && (
                  <p className="text-xs text-muted-foreground leading-relaxed">{scene.description}</p>
                )}
                <div className="rounded-md bg-muted/40 border border-border p-2.5">
                  <p className="text-xs font-mono leading-relaxed text-foreground/80">{scene.prompt_text}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Inputs
// ---------------------------------------------------------------------------

/**
 * Strip a dev-time `http://localhost:port` prefix from an asset URL so the
 * browser fetches it relative to whatever host serves this page (works on
 * the prod IP). Leaves real external URLs untouched.
 */
function normalizeAssetUrl(url: string): string {
  return url.replace(/^https?:\/\/localhost:\d+/i, '')
}

function ReferenceThumb({
  url,
  kind,
  index,
}: {
  url: string
  kind: 'image' | 'video'
  index: number
}) {
  const src = normalizeAssetUrl(url)
  const label = kind === 'image' ? `Image ${index}` : `Video ${index}`
  return (
    <a
      href={src}
      target="_blank"
      rel="noopener noreferrer"
      className="group flex flex-col items-center gap-1"
      title={url}
    >
      {kind === 'image' ? (
        <img
          src={src}
          alt={label}
          loading="lazy"
          className="h-16 w-16 rounded-md border border-border object-cover group-hover:border-foreground/40 transition-colors"
        />
      ) : (
        <div className="flex h-16 w-16 items-center justify-center rounded-md border border-border bg-muted group-hover:border-foreground/40 transition-colors">
          <Video className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
        </div>
      )}
      <span className="text-[10px] text-muted-foreground font-mono">{label}</span>
    </a>
  )
}

function InputsTab({ job }: { job: JobDetailResponse }) {
  const params = job.input_params
  const imageUrls = params?.image_urls ?? []
  const videoUrls = params?.video_urls ?? []
  const hasContextAssets = job.context_assets && job.context_assets.length > 0
  const hasAvatarAsset = !!job.avatar_asset
  const hasAnyRef = imageUrls.length > 0 || videoUrls.length > 0

  // The Overview tab already surfaces the user's brief — this tab only
  // carries the engineered/technical prompt strings the model actually saw.
  const hasScript = !!job.script?.source_prompt
  const isStitched = job.output_kind === 'stitched'

  // For stitched-assembly jobs the parent job's prompt_text is just a
  // placeholder ("[Stitched: ...]") — the real engineered prompts live on
  // each scene. Fetch the assembly so we can render them inline.
  const [assembly, setAssembly] = React.useState<SceneAssembly | null>(null)
  React.useEffect(() => {
    if (!isStitched || !job.assembly_id) return
    let cancelled = false
    apiJson<SceneAssembly>(`/api/v1/assembly/${job.assembly_id}`)
      .then((data) => {
        if (!cancelled) setAssembly(data)
      })
      .catch(() => {
        /* silent — fallback message renders */
      })
    return () => {
      cancelled = true
    }
  }, [isStitched, job.assembly_id])

  return (
    <div className="space-y-6">
      {/* Engineered scene prompt — what the script engine produced from the
          user's brief and what was sent to the model. For stitched assembly
          jobs the parent prompt_text is a placeholder, so render the real
          per-scene prompts pulled from the assembly. */}
      {isStitched ? (
        assembly ? (
          <div className="space-y-4">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Engineered scene prompts ({assembly.scenes.length} scene{assembly.scenes.length !== 1 ? 's' : ''})
            </p>
            {[...assembly.scenes]
              .sort((a, b) => a.scene_number - b.scene_number)
              .map((scene) => (
                <div
                  key={scene.id}
                  className="rounded-md border border-border bg-muted/20 p-3 space-y-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      Scene {scene.scene_number}
                    </span>
                    <div className="flex items-center gap-1.5">
                      {scene.style_profile && (
                        <Badge variant="outline" className="text-[10px] font-mono">
                          {scene.style_profile.replace(/_/g, ' ')}
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-[10px]">
                        {scene.duration_seconds}s
                      </Badge>
                    </div>
                  </div>
                  <PromptBlock
                    label="Engineered prompt"
                    text={scene.prompt_text}
                  />
                  {scene.built_prompt &&
                    scene.built_prompt !== scene.prompt_text && (
                      <PromptBlock
                        label="Final prompt (style + context injected)"
                        text={scene.built_prompt}
                        variant="muted"
                      />
                    )}
                  {scene.bridge_frame_url && (
                    <div className="text-[11px] text-muted-foreground">
                      Bridge frame from previous scene attached as @Image1.
                    </div>
                  )}
                </div>
              ))}
          </div>
        ) : (
          <Skeleton className="h-32 w-full rounded-md" />
        )
      ) : (
        <>
          <PromptBlock
            label={hasScript ? 'Scene prompt (engineered)' : 'Prompt'}
            text={job.prompt_text}
          />

          {/* Built prompt — final prompt after style profile + context injection
              (only if it differs from the engineered scene prompt). */}
          {job.built_prompt && job.built_prompt !== job.prompt_text && (
            <PromptBlock
              label="Final prompt (style + context injected)"
              text={job.built_prompt}
              variant="muted"
            />
          )}
        </>
      )}

      {/* Negative prompt */}
      {params?.negative_prompt && (
        <PromptBlock
          label="Negative Prompt"
          text={params.negative_prompt}
          variant="muted"
        />
      )}

      {/* Avatar asset */}
      {hasAvatarAsset && (
        <>
          <Separator />
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Avatar</p>
            <div className="flex items-center gap-3">
              {job.avatar_asset!.file_type === 'image' && (
                <img
                  src={job.avatar_asset!.file_path}
                  alt={job.avatar_asset!.filename}
                  className="h-16 w-16 rounded-md border border-border object-cover"
                  loading="lazy"
                />
              )}
              <div className="space-y-0.5">
                <p className="text-sm font-medium">{job.avatar_asset!.filename}</p>
                {job.avatar_asset!.ai_description && (
                  <p className="text-xs text-muted-foreground">{job.avatar_asset!.ai_description}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Context assets */}
      {hasContextAssets && (
        <>
          <Separator />
          <div className="space-y-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Context Assets ({job.context_assets.length})
            </p>
            <div className="flex flex-wrap gap-2">
              {job.context_assets.map((asset) => (
                <div key={asset.id} className="flex flex-col items-center gap-1">
                  {asset.file_type === 'image' ? (
                    <img
                      src={asset.file_path}
                      alt={asset.filename}
                      title={`${asset.filename}${asset.folder_name ? ` — ${asset.folder_name}` : ''}`}
                      className="h-14 w-14 rounded-md border border-border object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-md border border-border bg-muted">
                      <Video className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                    </div>
                  )}
                  {asset.folder_name && (
                    <span className="max-w-[56px] truncate text-[10px] text-muted-foreground">
                      {asset.folder_name}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Reference URLs */}
      {hasAnyRef && (
        <>
          <Separator />
          <div className="space-y-3">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Reference Inputs
            </p>
            {imageUrls.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <ImageIcon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  <span className="text-xs text-muted-foreground">
                    Images ({imageUrls.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 pl-5">
                  {imageUrls.map((url, i) => (
                    <ReferenceThumb key={`img-${i}`} url={url} kind="image" index={i + 1} />
                  ))}
                </div>
              </div>
            )}
            {videoUrls.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-1.5">
                  <Video className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                  <span className="text-xs text-muted-foreground">
                    Videos ({videoUrls.length})
                  </span>
                </div>
                <div className="flex flex-wrap gap-2 pl-5">
                  {videoUrls.map((url, i) => (
                    <ReferenceThumb key={`vid-${i}`} url={url} kind="video" index={i + 1} />
                  ))}
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* All empty */}
      {!hasAvatarAsset && !hasContextAssets && !hasAnyRef && !params?.negative_prompt && (
        <p className="text-sm text-muted-foreground">No additional inputs attached to this job.</p>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Outputs
// ---------------------------------------------------------------------------

/** Normalize a stored video path to a usable browser URL.
 *  Backend stores per-scene URLs as "/uploads/generated/abc.mp4" but the
 *  assembly's output_path as "uploads/assemblies/.../stitched.mp4" (no
 *  leading slash). Older stitched jobs were also saved with a hard-coded
 *  "http://localhost:8000/uploads/..." prefix because BASE_URL wasn't set
 *  on prod — strip that so the browser fetches relative to whatever host
 *  serves this page, avoiding the CORS / private-address-space block. */
function videoSrc(pathOrUrl: string | null | undefined): string {
  if (!pathOrUrl) return ''
  // Strip ANY absolute origin so the asset resolves against the current
  // host. Prevents URLs baked with localhost, a hard-coded server IP
  // (e.g. http://91.99.102.124:7500/...), or a stale public domain from
  // 404'ing in the browser when the user accesses the app via a
  // different hostname. The path (with /uploads/...) is served by the
  // same backend that's responding to this page, so this always works.
  let path = pathOrUrl
  if (/^https?:\/\//i.test(pathOrUrl)) {
    try {
      const u = new URL(pathOrUrl)
      path = u.pathname + u.search + u.hash
    } catch {
      /* malformed — fall through and treat as a path */
    }
  }
  if (path.startsWith('/uploads/')) return path
  if (path.startsWith('uploads/')) return '/' + path
  if (path.startsWith('/')) return path
  return '/uploads/' + path.replace(/^\/+/, '')
}

function sceneStatusClass(status: string): string {
  switch (status) {
    case 'completed':
      return 'bg-green-500/10 text-green-600 border-green-500/30'
    case 'processing':
      return 'bg-orange-400/10 text-orange-500 border-orange-400/30 animate-pulse'
    case 'failed':
      return 'bg-red-500/10 text-red-500 border-red-500/30'
    case 'cancelled':
      return 'bg-zinc-500/10 text-zinc-500 border-zinc-500/30'
    case 'queued':
    case 'pending':
    default:
      return 'bg-red-500/10 text-red-500 border-red-500/30'
  }
}

interface AssemblyOutputViewProps {
  assemblyId: number
  /** Job currently being viewed — used to pre-select the matching item on the right. */
  currentJob: JobDetailResponse
}

function AssemblyOutputView({ assemblyId, currentJob }: AssemblyOutputViewProps) {
  const [assembly, setAssembly] = React.useState<SceneAssembly | null>(null)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [selected, setSelected] = React.useState<'stitched' | number>(
    currentJob.output_kind === 'scene' && currentJob.scene_number != null
      ? currentJob.scene_number
      : 'stitched',
  )

  const fetchAssembly = React.useCallback(async () => {
    try {
      const data = await apiJson<SceneAssembly>(`/api/v1/assembly/${assemblyId}`)
      setAssembly(data)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [assemblyId])

  React.useEffect(() => {
    fetchAssembly()
  }, [fetchAssembly])

  // Poll while anything is still generating
  React.useEffect(() => {
    if (!assembly) return
    const isActive =
      assembly.status === 'generating' ||
      assembly.status === 'stitching' ||
      assembly.scenes.some((s) => s.status === 'queued' || s.status === 'processing' || s.status === 'pending')
    if (!isActive) return
    const interval = window.setInterval(fetchAssembly, 5000)
    return () => window.clearInterval(interval)
  }, [assembly, fetchAssembly])

  if (loading) {
    return <Skeleton className="h-72 w-full rounded-lg" />
  }

  if (error || !assembly) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error ?? 'Assembly not found.'}
      </div>
    )
  }

  const stitched = assembly.output_path ?? null
  const sortedScenes = [...assembly.scenes].sort((a, b) => a.scene_number - b.scene_number)

  const selectedScene =
    selected !== 'stitched'
      ? sortedScenes.find((s) => s.scene_number === selected) ?? null
      : null
  const playerSrc = videoSrc(
    selected === 'stitched' ? stitched : selectedScene?.output_url ?? null,
  )

  const playerTitle =
    selected === 'stitched'
      ? 'Final cut (stitched)'
      : selectedScene
        ? `Scene ${selectedScene.scene_number}`
        : 'Output'

  // Highest-level error surface: when the assembly itself failed, show a
  // banner up top so the user doesn't have to click through scenes to learn
  // which ones broke. Per-scene errors still live in the chips + the player
  // placeholder below.
  const assemblyFailed = assembly.status === 'failed'
  const failedScenes = sortedScenes.filter((s) => s.status === 'failed')

  return (
    <div className="space-y-3">
      {assemblyFailed && (
        <div
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive space-y-1.5"
        >
          <p className="font-semibold">
            Assembly failed
            {failedScenes.length > 0 && (
              <span className="font-normal text-destructive/80">
                {' '}
                — {failedScenes.length} scene{failedScenes.length === 1 ? '' : 's'} failed:{' '}
                {failedScenes.map((s) => `#${s.scene_number}`).join(', ')}
              </span>
            )}
          </p>
          {assembly.error_message && (
            <p className="text-xs text-destructive/85">{assembly.error_message}</p>
          )}
          {failedScenes.length > 0 && (
            <p className="text-xs text-destructive/80">
              Click each failed scene on the right to see its full error message.
            </p>
          )}
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-[2fr_1fr]">
        {/* Left — player */}
        <div className="space-y-2 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{playerTitle}</p>
            {playerSrc && (
              <DownloadButton
                url={playerSrc}
                filename={
                  selected === 'stitched'
                    ? `creativeos-assembly-${assembly.id}-stitched.mp4`
                    : `creativeos-assembly-${assembly.id}-scene-${selected}.mp4`
                }
              />
            )}
          </div>
          {playerSrc ? (
            <video
              key={playerSrc}
              src={playerSrc}
              className="w-full rounded-lg border border-border bg-black"
              controls
              preload="metadata"
              playsInline
              aria-label={playerTitle}
            />
          ) : (
            // Failed-scene state shows the actual error message instead of a
            // generic "Scene not ready yet." placeholder. Pulls from
            // selectedScene.error_message (per-scene) when a scene is
            // selected, and falls back to assembly-level error_message on
            // the Final-cut tab when the stitch never landed.
            <div
              className={`w-full aspect-video rounded-lg border ${
                (selectedScene?.status === 'failed') ||
                (selected === 'stitched' && assembly.status === 'failed')
                  ? 'border-destructive/40 bg-destructive/5'
                  : 'border-dashed border-border bg-muted/30'
              } flex items-center justify-center p-4`}
            >
              {selectedScene?.status === 'failed' && selectedScene.error_message ? (
                <div className="space-y-1.5 text-center max-w-md">
                  <p className="text-xs font-semibold uppercase tracking-wider text-destructive">
                    Scene {selectedScene.scene_number} failed
                  </p>
                  <p className="text-xs text-destructive/90 whitespace-pre-wrap break-words">
                    {selectedScene.error_message}
                  </p>
                </div>
              ) : selected === 'stitched' && assembly.status === 'failed' ? (
                <div className="space-y-1.5 text-center max-w-md">
                  <p className="text-xs font-semibold uppercase tracking-wider text-destructive">
                    Assembly failed
                  </p>
                  <p className="text-xs text-destructive/90 whitespace-pre-wrap break-words">
                    {assembly.error_message || 'See per-scene errors on the right.'}
                  </p>
                </div>
              ) : (
                <span className="text-xs text-muted-foreground">
                  {selected === 'stitched' ? 'Stitched cut not ready yet.' : 'Scene not ready yet.'}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Right — scene list */}
        <div className="space-y-1.5">
          <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Scenes</p>
          {stitched != null && (
            <button
              type="button"
              onClick={() => setSelected('stitched')}
              className={`w-full text-left rounded-md border px-3 py-2 transition-colors hover:bg-muted/50 ${selected === 'stitched' ? 'border-foreground/60 bg-muted/40' : 'border-border'}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium inline-flex items-center gap-1.5">
                  <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                  Final cut
                </span>
                <Badge variant="outline" className={`text-[10px] ${sceneStatusClass(assembly.status)}`}>
                  {assembly.status}
                </Badge>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">Stitched output</p>
            </button>
          )}
          {sortedScenes.map((scene) => {
            const active = selected === scene.scene_number
            const isFailed = scene.status === 'failed'
            return (
              <button
                key={scene.id}
                type="button"
                onClick={() => setSelected(scene.scene_number)}
                className={`w-full text-left rounded-md border px-3 py-2 transition-colors hover:bg-muted/50 ${
                  active
                    ? isFailed
                      ? 'border-destructive/60 bg-destructive/10'
                      : 'border-foreground/60 bg-muted/40'
                    : isFailed
                      ? 'border-destructive/30'
                      : 'border-border'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium">Scene {scene.scene_number}</span>
                  <Badge variant="outline" className={`text-[10px] ${sceneStatusClass(scene.status)}`}>
                    {scene.status}
                  </Badge>
                </div>
                {/* Failed scenes surface the actual error in the chip so the
                    user can scan reasons without expanding each one. */}
                {isFailed && scene.error_message ? (
                  <p className="text-[11px] text-destructive/90 mt-0.5 line-clamp-2">
                    {scene.error_message}
                  </p>
                ) : (
                  <p className="text-[11px] text-muted-foreground mt-0.5 line-clamp-2">
                    {scene.prompt_text}
                  </p>
                )}
              </button>
            )
          })}
        </div>
      </div>

      {/* Full engineered prompt for the active scene — surfaces exactly what
          was sent to Seedance. Stitched/Final-cut selection skips this since
          the stitched job carries a placeholder, not a real Seedance prompt. */}
      {selectedScene && (
        <div className="space-y-3 pt-2">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Scene {selectedScene.scene_number} — engineered prompt (sent to Seedance)
            </p>
            <Badge variant="outline" className="text-[10px]">
              {selectedScene.duration_seconds}s
            </Badge>
          </div>
          <pre className="rounded-md bg-muted/40 border border-border p-3 text-xs font-mono leading-relaxed text-foreground/80 whitespace-pre-wrap break-words">
            {selectedScene.prompt_text}
          </pre>
          {selectedScene.built_prompt && selectedScene.built_prompt !== selectedScene.prompt_text && (
            <>
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                Final prompt (style + context injected)
              </p>
              <pre className="rounded-md bg-muted/20 border border-border p-3 text-xs font-mono leading-relaxed text-muted-foreground whitespace-pre-wrap break-words">
                {selectedScene.built_prompt}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab: Bridge — visible only on assembly jobs. Shows each scene-to-scene
// handoff: the last-frame extracted from scene N and fed as @Image1 to
// scene N+1 (via bridge_frame_url). For assemblies generated without
// bridging, displays a friendly empty state.
// ---------------------------------------------------------------------------

function BridgeTab({ assemblyId }: { assemblyId: number }) {
  const [assembly, setAssembly] = React.useState<SceneAssembly | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    let cancelled = false
    apiJson<SceneAssembly>(`/api/v1/assembly/${assemblyId}`)
      .then((data) => {
        if (!cancelled) {
          setAssembly(data)
          setError(null)
        }
      })
      .catch((err) => {
        if (!cancelled) setError((err as Error).message)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [assemblyId])

  if (loading) return <Skeleton className="h-48 w-full rounded-md" />
  if (error || !assembly) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
        {error ?? 'Assembly not found.'}
      </div>
    )
  }

  const params = (assembly.generation_params ?? {}) as Record<string, unknown>
  const bridgeEnabled = params.bridge_enabled === true
  const sortedScenes = [...assembly.scenes].sort((a, b) => a.scene_number - b.scene_number)
  const transitions = sortedScenes.slice(1) // each represents the bridge from scene N-1 → N

  if (!bridgeEnabled && !transitions.some((s) => s.bridge_frame_url)) {
    return (
      <div className="rounded-md border border-border bg-muted/20 p-4 text-sm text-muted-foreground space-y-1">
        <p className="font-medium text-foreground">No bridge frames for this assembly.</p>
        <p>
          Bridge chaining was off, so every scene was dispatched in parallel with no
          inter-scene visual anchor. Identity continuity relied entirely on the
          script's text-side instructions.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-border bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">Bridge chaining was ON.</span>{' '}
        Each scene's last frame was extracted and fed as <span className="font-mono">@Image1</span> to
        the next scene to lock identity, outfit, and setting across cuts.
      </div>

      <div className="space-y-3">
        {transitions.map((scene) => {
          const prev = sortedScenes.find((s) => s.scene_number === scene.scene_number - 1)
          const bridgeSrc = videoSrc(scene.bridge_frame_url) || null
          return (
            <Card key={scene.id}>
              <CardHeader>
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  Scene {prev?.scene_number ?? '?'}
                  <span className="text-muted-foreground">→</span>
                  Scene {scene.scene_number}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {bridgeSrc ? (
                  <a
                    href={bridgeSrc}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full max-w-md rounded-md overflow-hidden border border-border hover:border-foreground/30"
                  >
                    <img
                      src={bridgeSrc}
                      alt={`Bridge frame from scene ${prev?.scene_number ?? '?'}`}
                      className="w-full h-auto"
                      loading="lazy"
                    />
                  </a>
                ) : (
                  <p className="text-xs text-muted-foreground italic">
                    No bridge frame captured for this transition — likely a
                    face-moderation hit that triggered the bridge_poisoned
                    fallback (scene re-dispatched with no bridge).
                  </p>
                )}
                <p className="text-[11px] text-muted-foreground">
                  Frame extracted ~1.5–2.5s before scene {prev?.scene_number ?? '?'} ended.
                  Face pixelation applied before handoff.
                </p>
              </CardContent>
            </Card>
          )
        })}
      </div>
    </div>
  )
}

function OutputsTab({ job, onRegenerate, regenerating }: { job: JobDetailResponse; onRegenerate: () => void; regenerating?: boolean }) {
  // Strip localhost prefixes so videos saved with a hard-coded BASE_URL
  // still play on the prod host (private-address-space CORS block).
  const videoUrl = job.output_type === 'video' ? videoSrc(job.output_url) || null : null
  const imageUrl = job.output_type === 'image' ? videoSrc(job.output_url) || null : null
  const isAssembly = job.assembly_id != null
  const hasOutput = videoUrl || imageUrl

  return (
    <div className="space-y-6">
      {isAssembly && job.assembly_id != null ? (
        <AssemblyOutputView assemblyId={job.assembly_id} currentJob={job} />
      ) : (
        <>
          {!hasOutput && (
            <EmptySection message="No outputs yet for this job." />
          )}

          {videoUrl && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Video Output</p>
              <video
                src={videoUrl}
                className="w-full rounded-lg border border-border bg-black"
                controls
                preload="metadata"
                aria-label="Generated video"
              />
              <div className="flex flex-wrap gap-2">
                <DownloadButton url={videoUrl} filename={`creativeos-job-${job.id}.mp4`} />
                <SaveToLibrary jobId={job.id} />
              </div>
            </div>
          )}

          {imageUrl && (
            <div className="space-y-2">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Image Output</p>
              <img
                src={imageUrl}
                alt="Generated output"
                className="w-full rounded-lg border border-border"
                loading="lazy"
              />
              <div className="flex flex-wrap gap-2">
                <DownloadButton url={imageUrl} filename={`creativeos-job-${job.id}.jpg`} />
                <SaveToLibrary jobId={job.id} />
              </div>
            </div>
          )}
        </>
      )}

      <Separator />

      <div className="space-y-2">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Regenerate</p>
        <p className="text-xs text-muted-foreground">
          Creates a new job with the same prompt and settings.
        </p>
        <Button variant="outline" size="sm" onClick={onRegenerate} disabled={regenerating}>
          {regenerating ? (
            <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" aria-hidden="true" />Submitting...</>
          ) : (
            <><RefreshCw className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />Regenerate</>
          )}
        </Button>
      </div>
    </div>
  )
}

const TERMINAL_STATUSES = new Set(['completed', 'failed', 'cancelled'])

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function JobDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()

  const [job, setJob] = React.useState<JobDetailResponse | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [starred, setStarred] = React.useState(false)
  const [regenerating, setRegenerating] = React.useState(false)

  const jobId = id ? Number(id) : null

  // ---------------------------------------------------------------------------
  // Fetch job detail
  // ---------------------------------------------------------------------------

  const fetchJob = React.useCallback(async () => {
    if (!jobId) return
    try {
      const data = await apiJson<JobDetailResponse>(`/api/v1/jobs/${jobId}`)
      setJob(data)
      setStarred(data.is_starred ?? false)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [jobId])

  // Initial fetch
  React.useEffect(() => {
    fetchJob()
  }, [fetchJob])

  // Polling while active
  React.useEffect(() => {
    if (!jobId) return
    if (!job) return
    if (TERMINAL_STATUSES.has(job.status)) return

    const interval = window.setInterval(fetchJob, 5000)
    return () => window.clearInterval(interval)
  }, [jobId, job?.status, fetchJob])

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleToggleStar = async () => {
    if (!jobId) return
    const next = !starred
    setStarred(next)
    try {
      await apiFetch(`/api/v1/jobs/${jobId}/${next ? 'star' : 'unstar'}`, { method: 'POST' })
    } catch {
      setStarred(!next) // revert
      toast({ title: 'Error', description: 'Could not update star.', variant: 'destructive' })
    }
  }

  const handleCancel = async () => {
    if (!jobId) return
    try {
      const res = await apiFetch(`/api/v1/jobs/${jobId}/cancel`, { method: 'POST' })
      if (!res.ok) throw new Error('Cancel failed')
      toast({ title: 'Job cancelled' })
      fetchJob()
    } catch (err) {
      toast({ title: 'Error', description: (err as Error).message, variant: 'destructive' })
    }
  }

  const handleDelete = async () => {
    if (!jobId) return
    try {
      const res = await apiFetch(`/api/v1/jobs/${jobId}`, { method: 'DELETE' })
      if (!res.ok) throw new Error('Delete failed')
      toast({ title: 'Job deleted' })
      navigate('/jobs')
    } catch (err) {
      toast({ title: 'Error', description: (err as Error).message, variant: 'destructive' })
    }
  }

  const handleRegenerate = async () => {
    if (!job || regenerating) return
    setRegenerating(true)
    try {
      const req: GenerateRequest = {
        prompt: job.prompt_text,
        aspect_ratio: job.aspect_ratio,
        output_type: job.output_type,
        model_id: job.model_id,
        duration_seconds: job.duration_seconds,
        style_profile: job.style_profile,
        prompt_history_id: job.prompt_history_id ?? null,
        avatar_asset_id: job.avatar_asset_id ?? null,
        generate_audio: job.input_params?.generate_audio,
        negative_prompt: job.input_params?.negative_prompt ?? undefined,
      }
      const created = await apiJson<GenerationJob>('/api/v1/generate', {
        method: 'POST',
        body: JSON.stringify(req),
      })
      toast({ title: 'Regeneration queued', description: `New job #${created.id} submitted.` })
      navigate(`/created-assets/${created.id}`)
    } catch (err) {
      toast({ title: 'Failed to regenerate', description: (err as Error).message, variant: 'destructive' })
      setRegenerating(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  if (loading) {
    return (
      <div className="space-y-6 max-w-4xl animate-fade-in">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  if (error || !job) {
    return (
      <div className="space-y-4 max-w-4xl">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/created-assets">
            <ArrowLeft className="h-4 w-4 mr-1.5" aria-hidden="true" />
            Back to Created Assets
          </Link>
        </Button>
        <div
          className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive"
          role="alert"
        >
          {error ?? 'Job not found.'}
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-4xl animate-fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Button variant="ghost" size="sm" asChild className="-ml-2">
          <Link to="/created-assets">
            <ArrowLeft className="h-4 w-4 mr-1" aria-hidden="true" />
            Created Assets
          </Link>
        </Button>
        <span>/</span>
        <span className="text-foreground font-medium">Job #{job.id}</span>
        <span className="text-xs text-muted-foreground">{formatDate(job.created_at)}</span>
      </div>

      {/* Tabs */}
      <Tabs defaultValue={job.assembly_id != null ? 'outputs' : 'overview'}>
        <TabsList className="mb-2" aria-label="Job detail sections">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="script">
            Script
            {!job.script && (
              <span className="ml-1.5 text-[10px] text-muted-foreground">(none)</span>
            )}
          </TabsTrigger>
          <TabsTrigger value="inputs">Inputs</TabsTrigger>
          {job.assembly_id != null && (
            <TabsTrigger value="bridge">Bridge</TabsTrigger>
          )}
          <TabsTrigger value="outputs">Outputs</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab
            job={job}
            starred={starred}
            onToggleStar={handleToggleStar}
            onCancel={handleCancel}
            onDelete={handleDelete}
            onRegenerate={handleRegenerate}
            regenerating={regenerating}
          />
        </TabsContent>

        <TabsContent value="script" className="mt-4">
          <ScriptTab job={job} />
        </TabsContent>

        <TabsContent value="inputs" className="mt-4">
          <InputsTab job={job} />
        </TabsContent>

        {job.assembly_id != null && (
          <TabsContent value="bridge" className="mt-4">
            <BridgeTab assemblyId={job.assembly_id} />
          </TabsContent>
        )}

        <TabsContent value="outputs" className="mt-4">
          <OutputsTab job={job} onRegenerate={handleRegenerate} regenerating={regenerating} />
        </TabsContent>
      </Tabs>
    </div>
  )
}
